import {
  BitOperators,
  NumberOperators,
  SelectOperators,
  TextOperators,
} from "../config/formEnums";
import { getField, getLabelForJoinEntity } from "./searchFields";
import { FilterNode } from "./advancedSearch.types";
import { getSearchJoins } from "./advancedSearchRequest";

export interface OperatorOption {
  Key: string;
  Value: string;
}

const NoValueOperators = ["containsdata", "doesnotcontaindata"];
const ListOperators = ["in", "notin"];

/** The operators a field of this type offers, as the legacy filters did. */
export function getOperatorOptions(type?: string): OperatorOption[] {
  const operators =
    type === "bit"
      ? BitOperators
      : type === "select" || type === "picklist"
      ? SelectOperators
      : type === "int" || type === "money"
      ? NumberOperators
      : TextOperators;

  return Object.entries(operators).map(([Value, Key]) => ({
    Key: Key as string,
    Value,
  }));
}

export const operatorNeedsValue = (operator: string) =>
  !NoValueOperators.includes(operator.toLowerCase());

export const isListOperator = (operator: string) =>
  ListOperators.includes(operator);

type OperatorKind = "none" | "list" | "number" | "text" | "any";

const kindOf = (operator: string): OperatorKind | null => {
  switch (operator.toLowerCase()) {
    case "containsdata":
    case "doesnotcontaindata":
      return "none";
    case "in":
    case "notin":
      return "list";
    case "isgreaterthan":
    case "isgreaterthanorequalto":
    case "islessthan":
    case "islessthanorequalto":
      return "number";
    case "contains":
    case "doesnotcontain":
    case "beginswith":
    case "doesnotbeginwith":
    case "endswith":
    case "doesnotendwith":
      return "text";
    case "equals":
    case "doesnotequal":
      return "any";
    default:
      return null;
  }
};

/** Whether the value no longer fits once the operator changes, so it is cleared. */
export function shouldClearValue(from: string, to: string) {
  const oldKind = kindOf(from);
  const newKind = kindOf(to);

  if (!oldKind || !newKind) {
    return true;
  }

  if (oldKind === newKind) {
    return false;
  }

  const compatible = ["number", "text", "any"];

  return !(
    (oldKind === "any" && compatible.includes(newKind)) ||
    (newKind === "any" && compatible.includes(oldKind))
  );
}

/** A new rule on the field the picker named, which is `alias.name` for a joined entity. */
export function createRule(
  entityName: string,
  fieldKey: string,
  allowedEntityTypes?: Record<string, string> | null
): FilterNode | null {
  const joins = getSearchJoins(entityName, allowedEntityTypes);
  const found = getField(
    [{ Key: "advancedSearchEntityName", Value: entityName }],
    joins,
    fieldKey
  );

  if (!found) {
    return null;
  }

  const { field, join } = found;

  return {
    ...field,
    alias: join ? join.Alias : "",
    label: join ? getLabelForJoinEntity(join, field.label) : field.label,
    name: field.linkedEntityId ?? fieldKey,
    nodeType: "Filter",
    operator: "equals",
    value: field.defaultValue ?? "",
  };
}
